'use client';

import {
  Button,
  EButtonColorVariant,
  EButtonSizeVariant,
  ETextStyleVariant,
  GeneralAlertCircleIcon,
  GeneralCameraOnIcon,
  GeneralCheckCircleIcon,
  GeneralCloseIcon,
  GeneralPaperClipIcon,
  GeneralUploadCloudIcon,
  Text,
} from '@outdoorsyco/bonfire';
import cn from 'classnames';
import React, { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useBreakpoint } from '@/hooks/useBreakpoint';
import apiRequest from '@/services/apiRequest';
import { logger } from '@/utility/logger';

type DocumentRequirement = 'Required' | 'If applicable';

type RequiredDocument = {
  label: string;
  requirement: DocumentRequirement;
};

type UploadStatus = 'uploading' | 'done' | 'error';

type DocumentsNeededProps = {
  adjusterName: string;
  claimNumber: string;
  documents?: Array<RequiredDocument>;
  acceptedFormats?: string;
  accept?: string;
  maxFileSizeMb?: number;
  onComplete?: () => void;
};

const DEFAULT_DOCUMENTS: Array<RequiredDocument> = [
  { label: 'Photos of the damage (minimum 4, all angles)', requirement: 'Required' },
  { label: 'Repair estimate from a licensed shop', requirement: 'Required' },
  { label: 'Police or incident report', requirement: 'If applicable' },
  { label: 'Proof of ownership (title or registration)', requirement: 'Required' },
];

const fileKey = (file: File) => `${file.name}-${String(file.size)}-${String(file.lastModified)}`;

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const StatusIndicator = ({
  status,
  fileName,
  onRemove,
}: {
  status?: UploadStatus;
  fileName: string;
  onRemove: () => void;
}) => {
  if (status === 'done') {
    return <GeneralCheckCircleIcon className="w-5 h-5 text-green-500 shrink-0" />;
  }
  if (status === 'error') {
    return <GeneralAlertCircleIcon className="w-5 h-5 text-red-600 shrink-0" />;
  }
  if (status === 'uploading') {
    return (
      <span className="w-4 h-4 border-2 rounded-full shrink-0 animate-spin border-canvas-200 border-t-canvas-300" />
    );
  }
  return (
    <button
      type="button"
      onClick={onRemove}
      aria-label={`Remove ${fileName}`}
      className="p-1 text-gray-500 rounded-full shrink-0 transition-colors hover:bg-gray-50 hover:text-gray-700">
      <GeneralCloseIcon className="w-4 h-4" />
    </button>
  );
};

const DocumentsNeeded = ({
  adjusterName,
  claimNumber,
  documents = DEFAULT_DOCUMENTS,
  acceptedFormats = 'PDF, JPG, PNG',
  accept = 'image/*,application/pdf',
  maxFileSizeMb = 25,
  onComplete,
}: DocumentsNeededProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<Array<File>>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [rejected, setRejected] = useState<Array<string>>([]);
  const [statuses, setStatuses] = useState<Record<string, UploadStatus>>({});
  const [isUploading, setIsUploading] = useState(false);
  const { isMobile } = useBreakpoint();

  const maxBytes = maxFileSizeMb * 1024 * 1024;

  const addFiles = (incoming: FileList | null) => {
    if (!incoming || incoming.length === 0) return;

    const tooLarge: Array<string> = [];
    const accepted = Array.from(incoming).filter(file => {
      if (file.size > maxBytes) {
        tooLarge.push(file.name);
        return false;
      }
      return true;
    });

    setRejected(tooLarge);
    setFiles(prev => {
      const seen = new Set(prev.map(fileKey));
      const deduped = accepted.filter(file => !seen.has(fileKey(file)));
      return [...prev, ...deduped];
    });
  };

  const removeFile = (key: string) => {
    setFiles(prev => prev.filter(file => fileKey(file) !== key));
    setStatuses(prev => {
      const next = { ...prev };

      delete next[key];
      return next;
    });
  };

  const onBrowseFiles = () => fileInputRef.current?.click();
  const onTakePhoto = () => cameraInputRef.current?.click();

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    addFiles(event.dataTransfer.files);
  };

  // Push the file to the claims service, then verify it returned the stored URL.
  const uploadFile = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('claim_number', claimNumber);

    const uploadData = await apiRequest<{ name: string; url: string }>(
      {
        url: `${process.env.brokerApi}/claims/${claimNumber}/uploads`,
        method: 'POST',
        data: formData,
      },
      true,
    );

    if (!uploadData?.url) {
      throw new Error('Attachment upload response missing URL');
    }
  };

  const handleSubmit = async () => {
    const pending = files.filter(file => statuses[fileKey(file)] !== 'done');
    if (pending.length === 0 || isUploading) return;

    setIsUploading(true);
    let hadFailure = false;

    for (const file of pending) {
      const key = fileKey(file);
      setStatuses(prev => ({ ...prev, [key]: 'uploading' }));
      try {
        await uploadFile(file);
        setStatuses(prev => ({ ...prev, [key]: 'done' }));
      } catch (error) {
        hadFailure = true;
        setStatuses(prev => ({ ...prev, [key]: 'error' }));
        logger.captureExceptionWithDatadog(
          error instanceof Error ? error : new Error('error uploading claim document'),
          { claimNumber, fileName: file.name },
        );
      }
    }

    setIsUploading(false);
    if (!hadFailure) {
      onComplete?.();
    }
  };

  const pendingCount = files.filter(file => statuses[fileKey(file)] !== 'done').length;
  const canSubmit = pendingCount > 0;

  return (
    <div className="mb-8 overflow-hidden border rounded-xl border-canvas-200 bg-canvas-100">
      {/* Header */}
      <div className="flex items-start px-5 pt-5 gap-3">
        <span className="flex items-center justify-center w-12 h-12 rounded-full shrink-0 bg-canvas-200">
          <GeneralPaperClipIcon className="w-5 h-5 text-yellow-800" />
        </span>
        <div>
          <Text variant={ETextStyleVariant.MediumBold} className="text-yellow-800">
            <FormattedMessage defaultMessage="Action Required — Documents Needed" id="ZEZwny" />
          </Text>
          <Text variant={ETextStyleVariant.SmallRegular} className="mt-1 text-gray-400">
            Your claim is on hold. We need a few things from you before {adjusterName} can continue
            the investigation. Upload them here — no need to email or call.
          </Text>
        </div>
      </div>

      <div className="mt-4 border-b border-canvas-200" />

      {/* Checklist */}
      <div className="px-5 pt-4">
        <Text
          variant={ETextStyleVariant.LegalBold}
          className="tracking-wide text-yellow-800 uppercase">
          What we need from you
        </Text>
        <ul className="flex flex-col mt-3">
          {documents.map(doc => (
            <li
              key={doc.label}
              className="flex items-center justify-between py-3 border-b gap-3 border-canvas-200">
              <span className="flex items-center gap-3">
                <span className="w-5 h-5 border-2 rounded shrink-0 border-canvas-300" />
                <Text variant={ETextStyleVariant.SmallRegular} className="text-gray-700">
                  {doc.label}
                </Text>
              </span>
              <Text
                variant={ETextStyleVariant.LegalBold}
                className={cn(
                  'shrink-0 uppercase tracking-wide',
                  doc.requirement === 'Required' ? 'text-yellow-800' : 'text-gray-400',
                )}>
                {doc.requirement}
              </Text>
            </li>
          ))}
        </ul>
      </div>

      {/* Upload */}
      <div className="px-5 pt-5">
        <Text
          variant={ETextStyleVariant.LegalBold}
          className="tracking-wide text-yellow-800 uppercase">
          Upload your files
        </Text>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={accept}
          className="hidden"
          onChange={event => {
            addFiles(event.target.files);
            event.target.value = '';
          }}
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={event => {
            addFiles(event.target.files);
            event.target.value = '';
          }}
        />

        <div
          onDragOver={event => {
            event.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => {
            setIsDragging(false);
          }}
          onDrop={handleDrop}
          className={cn(
            'mt-3 rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors',
            isDragging ? 'border-yellow-800 bg-canvas-200' : 'border-canvas-300',
          )}>
          <GeneralUploadCloudIcon className="w-8 h-8 mx-auto text-yellow-800" />
          <Text variant={ETextStyleVariant.SmallBold} className="mt-2 text-gray-700">
            <FormattedMessage defaultMessage="Drag files here or tap to browse" id="f3FiVM" />
          </Text>
          <Text variant={ETextStyleVariant.LegalRegular} className="mt-1 text-gray-400">
            {acceptedFormats} · Max {maxFileSizeMb} MB per file
          </Text>
          <div className="flex flex-wrap items-center justify-center mt-4 gap-3">
            <Button
              variant={EButtonColorVariant.Primary}
              size={EButtonSizeVariant.Small}
              onClick={onBrowseFiles}
              label="Browse Files"
            />
            {isMobile && (
              <Button
                variant={EButtonColorVariant.Secondary}
                size={EButtonSizeVariant.Small}
                onClick={onTakePhoto}
                label={
                  <span className="flex items-center gap-1.5">
                    <GeneralCameraOnIcon className="w-4 h-4" />{' '}
                    <FormattedMessage defaultMessage="Take Photo" id="bylW74" />
                  </span>
                }
              />
            )}
          </div>
        </div>

        {rejected.length > 0 && (
          <Text variant={ETextStyleVariant.LegalRegular} className="mt-2 text-red-600">
            {rejected.join(', ')} exceeded the {maxFileSizeMb} MB limit and{' '}
            {rejected.length === 1 ? 'was' : 'were'} skipped.
          </Text>
        )}

        {files.length > 0 && (
          <ul className="flex flex-col mt-3 gap-2">
            {files.map(file => {
              const key = fileKey(file);
              const status = statuses[key];
              return (
                <li
                  key={key}
                  className="flex items-center justify-between px-3 py-2 bg-white border rounded-lg gap-3 border-canvas-200">
                  <span className="min-w-0">
                    <Text variant={ETextStyleVariant.SmallBold} className="text-gray-700 truncate">
                      {file.name}
                    </Text>
                    <Text variant={ETextStyleVariant.LegalRegular} className="text-gray-400">
                      {formatBytes(file.size)}
                    </Text>
                  </span>
                  <StatusIndicator
                    status={status}
                    fileName={file.name}
                    onRemove={() => {
                      removeFile(key);
                    }}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Submit */}
      <div className="p-5">
        <Button
          variant={EButtonColorVariant.Primary}
          size={EButtonSizeVariant.Medium}
          fullWidth
          loading={isUploading}
          disabled={!canSubmit || isUploading}
          onClick={() => {
            void handleSubmit();
          }}
          label={
            isUploading
              ? 'Uploading…'
              : `Submit ${pendingCount > 0 ? `${String(pendingCount)} ` : ''}Document${
                  pendingCount === 1 ? '' : 's'
                } to ${adjusterName}`
          }
        />
      </div>
    </div>
  );
};

export default DocumentsNeeded;
